"use client";

import React, { useRef } from "react";

import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { useRouter } from "next/navigation";

import styles from "@/components/common/Stats/ScrollStat.module.scss";
import { ButtonNova } from "@/components/ui-kit/ButtonNova/ButtonNova";
import { TextElementNova } from "@/components/ui-kit/TextElementNova/TextElementNova";
import { ScrollStatProps } from "@/types/stats";
import { GA4 } from "@/utils/ga4Utils";
import { cn } from "@/utils/general";

const START_DISTANCE = 20; // start counting from the value - 20

const ScrollStat = ({
  stat,
  statsLength,
  statWidth,
  i,
  moduleName,
  colorMode = "dark",
  className,
  copyWrapperClassNames,
  copyClassNames,
}: ScrollStatProps) => {
  const statRef = useRef(null);
  const router = useRouter();

  const color = colorMode === "dark" ? "text-nova-base-white" : "text-nova-base-black";

  useGSAP(() => {
    const statNumber = statRef.current.querySelector(".stat-value");

    if (!statNumber) {
      return;
    }

    const targetNumber = parseFloat(String(stat.number));

    gsap.fromTo(
      statNumber,
      {
        textContent: Math.max(0, parseFloat(String(stat.number)) - START_DISTANCE),
      },
      {
        textContent: stat.number,
        duration: 2.5,
        ease: "power2.out",
        snap: { statNumber: 1 },
        onUpdate: function () {
          const currentValue = parseFloat(statNumber.textContent);
          statNumber.innerHTML = Number.isInteger(targetNumber)
            ? Math.ceil(currentValue).toLocaleString() // Integer: Round up
            : currentValue.toFixed(1).toLocaleString(); // Float: Show 1 decimal
        },
        scrollTrigger: {
          trigger: statRef.current,
          start: "top bottom",
        },
      }
    );
  }, []);

  const handleLinkClick = () => {
    GA4.onMarketingCtaClicked({
      text: stat.link.text,
      type: "button",
      module_location: "mid_fold",
      module_name: moduleName,
      destination: stat.link.destination,
    });
    router.push(stat.link.destination);
  };

  return (
    <div
      key={i}
      className={cn(
        styles.statFlex,
        `flex flex-wrap relative ${stat?.link ? "flex-start" : "center"}`,
        i === 0 ? styles.first : i === statsLength - 1 ? styles.last : "",
        colorMode === "light" && styles.light,
        className
      )}
      ref={statRef}
    >
      <div className={`${statWidth ? statWidth : ""}`}>
        <TextElementNova
          text={`<span class='stat-value font-grilliBook'>${stat.number}</span>${
            stat.suffix ? `<span class='font-grilliBook'>${stat.suffix}</span>` : ""
          }`}
          element="h3"
          classes={
            stat?.numberClasses || {
              default: "Title-30-Super-Normal",
              768: "Title-20-Super-Normal",
              1024: "Title-30-Super-Normal",
            }
          }
          className={`${color} stat-value-box`}
        />
      </div>

      <div
        className={cn(
          "flex flex-1 pl-2 lg:pl-6",
          stat?.link
            ? "items-start justify-between min-h-[82px] flex-col lg:min-h-[102px] min-[1520px]:justify-center"
            : "items-center",
          copyWrapperClassNames
        )}
      >
        <TextElementNova
          text={stat.copy}
          element="p"
          classes={
            stat?.copyClasses || {
              default: "Body-20-Dia-Normal",
            }
          }
          className={cn(color, "md:pr-4", copyClassNames)}
        />

        {stat.link ? (
          <div className={styles.buttonWrapper}>
            <ButtonNova
              icon={"Right"}
              level={"Textlink"}
              label={stat.link.text}
              onClick={handleLinkClick}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default ScrollStat;
